<template>
    <div>
        <!-- Normal banner (in-flow) -->
        <div
            v-show="!isSticky"
            ref="bannerRef"
            class="flex flex-col sm:flex-row w-full sm:items-center gap-4 sm:gap-8 px-4 sm:px-6 py-3 sm:py-4 rounded-lg bg-white dark:bg-slate-800"
            style="box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
        >
            <div class="flex items-start gap-3 flex-1">
                <Icon
                    icon="grid-add"
                    :width="20"
                    :height="20"
                    svg-class="text-slate-800 dark:text-slate-100 shrink-0 mt-0.5"
                />
                <div>
                    <p class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100">
                        Dashboard customization mode
                    </p>
                    <p class="text-sm font-normal leading-5 text-slate-500">
                        Rearrange widgets and set their visibility.
                    </p>
                </div>
            </div>
            <div class="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 rounded shadow-sm hover:bg-slate-50 transition-colors"
                    @click="dashboardStore.cancelEditMode()"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium leading-5 text-white rounded shadow-sm hover:opacity-90 transition-opacity whitespace-nowrap"
                    style="background: #FD421D"
                    :disabled="dashboardStore.widgetPreferencesSaving"
                    @click="dashboardStore.saveWidgetPreferences()"
                >
                    {{ dashboardStore.widgetPreferencesSaving ? 'Saving...' : 'Save changes' }}
                </button>
            </div>
        </div>

        <!-- Sticky banner (fixed, covers topbar) -->
        <div
            v-if="isSticky"
            class="fixed top-0 left-0 right-0 z-[101] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 px-4 sm:px-6 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 transition-all"
            :class="{ 'sm:left-64': appStore.sidebarOpen }"
        >
            <div class="flex items-start gap-3 flex-1">
                <Icon
                    icon="grid-add"
                    :width="20"
                    :height="20"
                    svg-class="shrink-0 mt-0.5"
                    path-class="fill-[#FD421D]"
                />
                <div>
                    <p class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100">
                        Dashboard customization mode
                    </p>
                    <p class="text-sm font-normal leading-5 text-slate-500">
                        Rearrange widgets and set visibility
                    </p>
                </div>
            </div>
            <div class="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 rounded shadow-sm hover:bg-slate-50 transition-colors"
                    @click="dashboardStore.cancelEditMode()"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium leading-5 text-white rounded shadow-sm hover:opacity-90 transition-opacity whitespace-nowrap"
                    style="background: #FD421D"
                    :disabled="dashboardStore.widgetPreferencesSaving"
                    @click="dashboardStore.saveWidgetPreferences()"
                >
                    {{ dashboardStore.widgetPreferencesSaving ? 'Saving...' : 'Save changes' }}
                </button>
            </div>
        </div>

        <!-- Spacer when sticky to prevent content jump -->
        <div v-if="isSticky" :style="{ height: bannerHeight + 'px' }"></div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useDashboardStore } from '@/store/dashboard';
import { useAppStore } from '@/store/app';

const dashboardStore = useDashboardStore();
const appStore = useAppStore();
const bannerRef = ref(null);
const isSticky = ref(false);
const bannerHeight = ref(0);
let scrollContainer = null;

function handleScroll() {
    if (bannerRef.value && !isSticky.value) {
        const rect = bannerRef.value.getBoundingClientRect();
        if (rect.top < 0) {
            isSticky.value = true;
        }
    } else if (isSticky.value && scrollContainer) {
        if (scrollContainer.scrollTop < 20) {
            isSticky.value = false;
        }
    }
}

onMounted(async () => {
    await nextTick();
    if (bannerRef.value) {
        bannerHeight.value = bannerRef.value.offsetHeight;
    }
    scrollContainer = document.getElementById('f-body');
    if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
});

onBeforeUnmount(() => {
    if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
    }
});
</script>
